import React from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Reveal } from './Reveal';
const phases = [{
  days: 'Día 1-2',
  title: 'La desactivación',
  text: 'Tu cuerpo comienza a bajar la alarma. El cortisol no desaparece, simplemente deja de estar en máximo.'
}, {
  days: 'Día 3-5',
  title: 'El drenaje',
  text: 'Con el cortisol más bajo, tus riñones reciben la orden: “no necesitamos retener sodio”. El agua comienza a drenarse y aparecen los cambios más rápidos en el espejo.'
}, {
  days: 'Día 6-7',
  title: 'La reconfiguración',
  text: 'Tu digestión se reactiva. Tu metabolismo se normaliza. Tu cuerpo entiende que no hay amenaza.'
}];
const curve = [{
  dia: 'Día 1',
  alerta: 92,
  protocolo: 88
}, {
  dia: 'Día 2',
  alerta: 94,
  protocolo: 78
}, {
  dia: 'Día 3',
  alerta: 90,
  protocolo: 64
}, {
  dia: 'Día 4',
  alerta: 95,
  protocolo: 52
}, {
  dia: 'Día 5',
  alerta: 93,
  protocolo: 43
}, {
  dia: 'Día 6',
  alerta: 96,
  protocolo: 36
}, {
  dia: 'Día 7',
  alerta: 94,
  protocolo: 30
}];
export function HowItWorks() {
  return <section className="w-full bg-sand/50 py-16 sm:py-24" aria-labelledby="mecanismo-title" data-id="element-160" data-mp="2mnmj345y59s7">
      <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-161" data-mp="31civteiy7az5">
        <Reveal data-id="element-162" data-mp="2wkr4v2vvr38h">
          <p className="text-xs font-semibold uppercase tracking-widest text-clay" data-id="element-163" data-mp="2wk8z9fnbvcnw">
            El Código Cortisol explicado
          </p>
          <h2 id="mecanismo-title" className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight text-bark sm:text-[2.6rem]" data-id="element-164" data-mp="3aaksxizk9jh5">
            Cómo funcionan los 7 días
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-ink/85 sm:text-lg" data-id="element-165" data-mp="3k9tm49mlqu2g">
            Código Cortisol no es una dieta. Es un{' '}
            <strong className="font-semibold text-bark" data-id="element-166" data-mp="1pw2bb6sje45f">Protocolo de Seguridad Biológica</strong>. En
            lugar de atacar tu cuerpo, le enviamos la señal opuesta: “estamos a salvo”.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14" data-id="element-167" data-mp="3l08tsr42jtoq">
          <Reveal data-id="element-168" data-mp="3pxs81r0ro6go">
            <ol className="relative space-y-6 border-l border-bark/15 pl-8" data-id="element-169" data-mp="3pzd3diuwntfl">
              {phases.map(phase => <li key={phase.days} className="relative" data-id="element-170" data-mp="3tzdm3fp1eqsg">
                  <span className="absolute -left-[41px] flex h-6 w-6 items-center justify-center rounded-full bg-sageDeep text-[11px] font-bold text-cream" aria-hidden="true" data-id="element-171" data-mp="2udm3bm38joj">
                    ✓
                  </span>
                  <p className="text-xs font-semibold uppercase tracking-widest text-clay" data-id="element-172" data-mp="cr06y0eezaqk">
                    {phase.days}
                  </p>
                  <h3 className="mt-1 font-display text-xl text-bark sm:text-2xl" data-id="element-173" data-mp="hl4eakrldks6">{phase.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/80" data-id="element-174" data-mp="lm6dx2pf7yuo">{phase.text}</p>
                </li>)}
            </ol>
          </Reveal>

          <Reveal delay={0.05} data-id="element-175" data-mp="vjkvx6qzsvp3">
            <figure className="rounded-2xl border border-bark/10 bg-cream p-5 shadow-soft sm:p-6" data-id="element-176" data-mp="visxlo60gjv4">
              <figcaption className="mb-4" data-id="element-177" data-mp="15nrl0abls1m5">
                <h3 className="font-display text-lg text-bark" data-id="element-178" data-mp="10mu2txqlf7kn">
                  Curva de cortisol durante los 7 días
                </h3>
                <p className="mt-1 text-xs text-ink/60" data-id="element-179" data-mp="vosjdbp9968n">
                  Representación ilustrativa del comportamiento hormonal descrito en el protocolo.
                </p>
              </figcaption>
              <div className="h-64 w-full" data-id="element-180" data-mp="1ocg1mk31zks4">
                <ResponsiveContainer width="100%" height="100%" data-id="element-181" data-mp="1je4q84x4127s">
                  <AreaChart data={curve} margin={{
                  top: 8,
                  right: 8,
                  left: -20,
                  bottom: 0
                }} data-id="element-182" data-mp="1jemspjad8co5">
                    <defs data-id="element-183" data-mp="1al1go7pl59if">
                      <linearGradient id="alertaFill" x1="0" y1="0" x2="0" y2="1" data-id="element-184" data-mp="1ajqcw7w541rb">
                        <stop offset="0%" stopColor="#B4553C" stopOpacity={0.25} data-id="element-185" data-mp="274qnr29hn43j" />
                        <stop offset="100%" stopColor="#B4553C" stopOpacity={0.02} data-id="element-186" data-mp="22b5xxxi1tgff" />
                      </linearGradient>
                      <linearGradient id="protocoloFill" x1="0" y1="0" x2="0" y2="1" data-id="element-187" data-mp="1mwsif70zof0d">
                        <stop offset="0%" stopColor="#5C6B50" stopOpacity={0.25} data-id="element-188" data-mp="1rv3pxb3tadff" />
                        <stop offset="100%" stopColor="#5C6B50" stopOpacity={0.02} data-id="element-189" data-mp="1dvceni4ojb54" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#2E241D" strokeOpacity={0.08} vertical={false} data-id="element-190" data-mp="25l74dfwg92ub" />
                    <XAxis dataKey="dia" tick={{
                    fill: '#4A3D33',
                    fontSize: 11
                  }} tickLine={false} axisLine={{
                    stroke: '#2E241D',
                    strokeOpacity: 0.12
                  }} data-id="element-191" data-mp="2akacxabn1r71" />
                    <YAxis tick={{
                    fill: '#4A3D33',
                    fontSize: 11
                  }} tickLine={false} axisLine={false} domain={[0, 100]} data-id="element-192" data-mp="20sntwrt26ntq" />
                    <Tooltip contentStyle={{
                    background: '#FBF7F0',
                    border: '1px solid rgba(46,36,29,0.12)',
                    borderRadius: 12,
                    fontSize: 12,
                    color: '#2E241D'
                  }} data-id="element-193" data-mp="2tbl29n1ptvwt" />
                    <Legend wrapperStyle={{
                    fontSize: 12,
                    color: '#4A3D33'
                  }} data-id="element-194" data-mp="rlvoddlte1hh" />
                    <Area type="monotone" dataKey="alerta" name="Sin protocolo" stroke="#B4553C" strokeWidth={2} fill="url(#alertaFill)" data-id="element-195" data-mp="mnavy0ct0m3l" />
                    <Area type="monotone" dataKey="protocolo" name="Con Código Cortisol" stroke="#5C6B50" strokeWidth={2} fill="url(#protocoloFill)" data-id="element-196" data-mp="3q3hq9g9bx6zp" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>;
}